import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MyWorkspaceTable from '../MyWorkspaceTable';
import intl from 'react-intl-universal';
import get from 'lodash/get';
import { Request, AvatarUser } from 'utils/customTypes';
import UserAvatar from 'Atoms/UserAvatar';
import RequestLink from 'Atoms/RequestLink';
import { getRequestRoute, navigateToRequest } from 'utils/dynamicRoutes';
import { selectOrganizationId } from 'state/User/userSlice';
import { tailwindOverride } from '@getsynapse/design-system';
import { isUserStatusDisabled } from 'utils/functions';
import { formatRequestLearningPriority } from 'utils/formatters';

type RequestsTableProps = {
  data: Request[];
  handleSort: (orderByParam: string[], order: 'desc' | 'asc') => void;
  cardTable: boolean;
};

const RequestsTable = ({ data, handleSort, cardTable }: RequestsTableProps) => {
  const history = useHistory();
  const organizationId = useSelector(selectOrganizationId);

  return (
    <MyWorkspaceTable
      cardTable={cardTable}
      dataCy='requests-in-review-table'
      headCells={[
        {
          content: intl.get(
            'MY_WORKSPACE.IN_REVIEW_REQUESTS.TABLE.HEAD_CELLS.TITLE'
          ),
          columnName: 'title',
        },
        ...(!cardTable
          ? [
              {
                content: intl.get(
                  'MY_WORKSPACE.IN_REVIEW_REQUESTS.TABLE.HEAD_CELLS.DAYS_IN_REVIEW'
                ),
                columnName: 'daysInReview',
                className: 'w-32',
              },
            ]
          : []),
        {
          content: intl.get(
            'MY_WORKSPACE.IN_REVIEW_REQUESTS.TABLE.HEAD_CELLS.REQUESTER'
          ),
          columnName: 'requesterName',
          className: 'w-48',
        },
        {
          content: intl.get(
            'MY_WORKSPACE.IN_REVIEW_REQUESTS.TABLE.HEAD_CELLS.PRIORITY'
          ),
          columnName: 'ldPriority',
          className: 'w-28',
        },
      ]}
      rows={data
        .filter((request): request is Request & { id: string } => !!request.id)
        .map((request) => {
          const requestHref = getRequestRoute(request.id, organizationId, {
            isJsonSchema: request.isJsonSchema,
          });

          return {
            id: request.id,
            className: 'table w-full table-fixed cursor-pointer',
            onClick: () =>
              navigateToRequest(history, requestHref, request.isJsonSchema),
            cells: [
              {
                content: (
                  <RequestLink
                    href={requestHref}
                    isJsonSchema={request.isJsonSchema}
                    title={request.title}
                  >
                    {request.title}
                  </RequestLink>
                ),
                className: 'truncate',
              },
              ...(!cardTable
                ? [
                    {
                      content: intl.get(
                        'MY_WORKSPACE.IN_REVIEW_REQUESTS.TABLE.DAYS',
                        { days: get(request, 'daysInReview') }
                      ),
                      className: 'w-32',
                    },
                  ]
                : []),
              {
                content: (
                  <div className='flex items-center'>
                    <UserAvatar
                      user={request.requester as AvatarUser}
                      size='small'
                    />
                    <span
                      className={tailwindOverride('ml-2.5', {
                        'text-neutral-light': isUserStatusDisabled(
                          request.requester.status
                        ),
                      })}
                    >
                      {get(request, 'requesterName')}
                    </span>
                  </div>
                ),
                className: 'w-48 py-1',
              },
              {
                // The raw ldPriority is an untranslated English value from the
                // API. `capitalize` is deliberately gone with it: the localized
                // labels are already correctly cased, and the CSS rule would
                // upper-case every word ("Non attribué" -> "Non Attribué").
                content: formatRequestLearningPriority(request.ldPriority),
                className: 'w-28',
              },
            ],
          };
        })}
      handleSort={handleSort}
    />
  );
};

export default RequestsTable;
